/**
 * Stored invocation specs read back into the invocation block's form values.
 *
 * Shared by the trigger form's invoke-action target and the ingestion rule
 * form, which both store an `ActionInvocationSpec`.
 */

import {
  fileSelectorsOf,
  stringifyCommand,
  type FileSelector,
  type InvocationInput,
} from "@/shared/domain/actions";
import {
  DatasetBinding,
  isTriggeringDatasetId,
  type ActionInvocationSpec,
} from "@/shared/domain/triggers";

import {
  type InputSourceValues,
  type InvocationSpecValues,
} from "./invocationSpecSchema";

/**
 * A stored invocation spec read back into form values: the inverse of
 * `buildInvocationSpec`, so a spec opened and saved unchanged is sent as it
 * was stored.
 */
export function invocationSpecValuesFromWire(
  spec: ActionInvocationSpec,
): InvocationSpecValues {
  const compute = spec.compute_requirement_overrides;
  const container = spec.container_parameter_overrides;
  const destination = spec.upload_destination ?? null;
  return {
    action: {
      name: spec.action.name,
      owner: spec.action.owner ?? null,
      digest: spec.action.digest ?? null,
    },
    parameters: { ...spec.parameter_values },
    inputs: inputRowsOf(spec.invocation_input),
    ...topicAndSessionSelectorsOf(spec.invocation_input),
    outputDestination:
      destination === null
        ? { mode: "source", datasetId: null }
        : { mode: "dataset", datasetId: destination.destination_id },
    overrides: {
      cpu: compute?.vCPU?.toString(),
      memory: compute?.memory?.toString(),
      gpu:
        compute?.gpu === undefined || compute.gpu === null
          ? undefined
          : String(compute.gpu),
      storage: compute?.storage ?? undefined,
      timeout: spec.timeout ?? undefined,
      workdir: container?.workdir ?? undefined,
      command: container?.command
        ? stringifyCommand(container.command)
        : undefined,
      entrypoint: container?.entry_point
        ? stringifyCommand(container.entry_point)
        : undefined,
      envVars: Object.entries(container?.env_vars ?? {}).map(
        ([key, value]) => ({ key, value }),
      ),
    },
  };
}

function isNonEmpty(values: readonly string[] | null): values is string[] {
  return values !== null && values.length > 0;
}

/** `value` as a RoboQL string literal, which escapes only `\` and `"`. */
function roboqlString(value: string): string {
  return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

/**
 * The topic and session selectors a stored input holds, carried whole since
 * the form has no rows for them. Each key is kept only as stored, so a spec
 * without `sessions` saves back without it.
 */
function topicAndSessionSelectorsOf(
  input: InvocationInput | null | undefined,
): Pick<InvocationSpecValues, "topicAndSessionSelectors"> {
  if (
    input === null ||
    input === undefined ||
    (isAbsent(input.topics) && isAbsent(input.sessions))
  ) {
    return {};
  }
  return {
    topicAndSessionSelectors: {
      topics: input.topics,
      ...(input.sessions === undefined ? {} : { sessions: input.sessions }),
    },
  };
}

function isAbsent(selector: unknown): boolean {
  return selector === null || selector === undefined;
}

/** A stored `invocation_input`'s file selectors read back as the rows that spell them. */
export function inputRowsOf(
  input: InvocationInput | null | undefined,
): InputSourceValues[] {
  return fileSelectorsOf(input).flatMap(selectorToRows);
}

/**
 * The rows one stored selector reads back as. The runtime looks up every
 * field a selector fills and gathers the files together, so a selector that
 * fills several is the same as one row for each.
 *
 * Files picked by id or by name have no row of their own and read back as the
 * query that finds them: for names, the very query the runtime builds. The
 * next save states them that way, which `query` is the preferred spelling of.
 */
function selectorToRows(selector: FileSelector): InputSourceValues[] {
  const rows: InputSourceValues[] = [];
  const paths = selector.paths ?? [];
  // An empty query is no query, which is how the runtime reads one.
  const query = selector.query === "" ? null : selector.query;
  const fillsAnotherField =
    query !== null || isNonEmpty(selector.ids) || isNonEmpty(selector.names);
  // The runtime reads a dataset only beside paths. On its own it is how the
  // form spells "every file"; beside another field it is ignored.
  if (
    selector.dataset_id !== null &&
    (paths.length > 0 || !fillsAnotherField)
  ) {
    rows.push(
      isTriggeringDatasetId(selector.dataset_id)
        ? { source: "triggering-dataset", globs: paths }
        : { source: "dataset", datasetId: selector.dataset_id, globs: paths },
    );
  }
  if (query !== null) {
    rows.push(queryToRow(query));
  }
  if (isNonEmpty(selector.ids)) {
    const text = selector.ids
      .map((id) => `file_id = ${roboqlString(id)}`)
      .join(" OR ");
    rows.push({ source: "query", text });
  }
  if (isNonEmpty(selector.names)) {
    const text = selector.names
      .map((name) => `path LIKE ${roboqlString(name)}`)
      .join(" OR ");
    rows.push({ source: "query", text });
  }
  return rows;
}

// A stored query can name its dataset inside the query text. It is read back
// as a dataset row, so the next save states it as the selector's own fields.
function queryToRow(query: string): InputSourceValues {
  const binding = DatasetBinding.fromQuery(query);
  if (binding === null) {
    return { source: "query", text: query };
  }
  return isTriggeringDatasetId(binding.datasetId)
    ? { source: "triggering-dataset", globs: [...binding.patterns] }
    : {
        source: "dataset",
        datasetId: binding.datasetId,
        globs: [...binding.patterns],
      };
}
